import React, { useEffect, useRef, useState } from 'react';
import 'jsoneditor/dist/jsoneditor.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConfig, saveConfig } from './redux/actions/configActions';
import { Box, Button, Typography } from '@mui/material';

const ConfigEditorWithJsonEditor = ({ configName }) => {
  const dispatch = useDispatch();
  const configData = useSelector(state => state.config.configs[configName]);
  const loading = useSelector(state => state.config.loading);
  const saving = useSelector(state => state.config.saving);
  const error = useSelector(state => state.config.error);

  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);

  // Track if conversion has been done
  const [converted, setConverted] = useState(false);

  // 1. Fetch on mount or configName change
  useEffect(() => {
    if (configName) {
      dispatch(fetchConfig(configName));
    }
  }, [dispatch, configName]);

  // 2. Instantiate JSONEditor once when data first arrives
  useEffect(() => {
    if (!configData || editorInstanceRef.current) return;

    let cancelled = false;

    (async () => {
      const { default: JSONEditor } = await import('jsoneditor');
      if (cancelled) return;

      editorInstanceRef.current = new JSONEditor(
        editorContainerRef.current,
        {
          mode: 'tree',
          onChange: () => {
            try {
              console.log('Editor changed:', editorInstanceRef.current.get());
            } catch {}
          },
        }
      );

      // initial render
      editorInstanceRef.current.set(configData);
    })();

    return () => {
      cancelled = true;
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
    };
  }, [configData]);

  // 3. Sync on every configData update (after save+re-fetch)
  useEffect(() => {
    if (editorInstanceRef.current && configData) {
      editorInstanceRef.current.set(configData);
    }
  }, [configData]);

  // Convert security object to ordered array
  const handleConvertToArray = () => {
    if (!editorInstanceRef.current) return;
    try {
      const data = editorInstanceRef.current.get();
      if (data.security && !Array.isArray(data.security)) {
        data.security = Object.entries(data.security).map(([name, schema]) => ({ name, schema }));
        if (data.defaultCriteria && !Array.isArray(data.defaultCriteria)) {
              data.defaultCriteria = Object.entries(data.defaultCriteria)
                 .map(([name, schema]) => ({ name, schema }));
             }  
        editorInstanceRef.current.set(data);
        setConverted(true);
      }
    } catch (err) {
      console.error('Error converting schema:', err);
    }
  };

  // 4. Save handler calls thunk which handles re-fetch
  const handleSave = () => {
    if (!editorInstanceRef.current) return;
    try {
      const updatedData = editorInstanceRef.current.get();
      dispatch(saveConfig(configName, updatedData));
    } catch (err) {
      console.error('Error saving JSON data:', err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
        <Typography>Loading configuration…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="h6" align="center" sx={{ mt: 4 }}>
        Error loading configuration: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Edit Configuration: {configName}
      </Typography>
      <Box
        ref={editorContainerRef}
        sx={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          height: 400,
          mt: 2,
        }}
      />
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleConvertToArray}
          disabled={converted}
        >
          {converted ? 'Converted' : 'Convert to Array'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default ConfigEditorWithJsonEditor;
